// =========================================================================
// Job board — projection only. Fork-owned (zone C), P5b lane.
//
// Port of the demo's `renderJobboard` (source line 3051) plus its two modes,
// `JB_MODE = 'summary' | 'board'`.
//
// The demo's own framing is worth keeping in mind while reading this file: the
// board is not a task list, it is *the operating model with the current jobs
// dropped into it*. A job's column is the module it has reached, and the
// summary row per module is what makes ten modules readable on one screen.
//
// Transformation layer (layer 3): pure functions over the engine's `jobs()`.
// No React, no fetching, no mutation.
//
// @see docs/logic/workflows/job-board.md
// =========================================================================

import type { MineCode } from '../../schema/machine';
import {
  MSHORT,
  type JobSeverity,
  type JobStage,
  type OemJob,
} from '../../shared/oem-engine';
import { MODULES, type OemModule } from '../../shared/seed/modules';
import { jobRunKey } from './job-nav';

// -------------------------------------------------------------------------
// Filters
// -------------------------------------------------------------------------

/** The board's URL state. Every field is optional and defaults to "all". */
export interface JobBoardFilters {
  /** `?mine=KG`. */
  readonly mine: MineCode | null;
  /** `?sev=crit`. */
  readonly sev: JobSeverity | null;
  /** `?kind=Alert` — matched against `OemJob.kind` exactly. */
  readonly kind: string | null;
}

const SEVERITIES: readonly JobSeverity[] = ['crit', 'warn', 'info', 'done'];

/** Parse the three filters out of a `URLSearchParams`. Unknown values drop. */
export function parseJobFilters(
  get: (key: string) => string | null,
  mines: readonly MineCode[],
  kinds: readonly string[],
): JobBoardFilters {
  const mine = get('mine');
  const sev = get('sev');
  const kind = get('kind');
  return {
    mine: mine && mines.includes(mine as MineCode) ? (mine as MineCode) : null,
    sev: sev && SEVERITIES.includes(sev as JobSeverity) ? (sev as JobSeverity) : null,
    kind: kind && kinds.includes(kind) ? kind : null,
  };
}

/** `true` when nothing is filtered out. */
export const noFilters = (f: JobBoardFilters): boolean =>
  f.mine === null && f.sev === null && f.kind === null;

/** Apply the filters. Order does not matter — all three are conjunctive. */
export const applyJobFilters = (
  jobs: readonly OemJob[],
  f: JobBoardFilters,
): OemJob[] =>
  jobs.filter(
    (j) =>
      (f.mine === null || j.mine === f.mine) &&
      (f.sev === null || j.sev === f.sev) &&
      (f.kind === null || j.kind === f.kind),
  );

/** Every `kind` present on the board, most common first — the filter menu. */
export function jobKinds(jobs: readonly OemJob[]): { kind: string; n: number }[] {
  const by = new Map<string, number>();
  for (const j of jobs) by.set(j.kind, (by.get(j.kind) ?? 0) + 1);
  return [...by.entries()]
    .map(([kind, n]) => ({ kind, n }))
    .sort((a, b) => b.n - a.n || a.kind.localeCompare(b.kind));
}

/** Every mine present on the board, in fleet order — the filter menu. */
export function jobMines(jobs: readonly OemJob[], order: readonly MineCode[]) {
  const present = new Set(jobs.map((j) => j.mine));
  return order.filter((m) => present.has(m)).map((m) => ({ mine: m, short: MSHORT[m] }));
}

// -------------------------------------------------------------------------
// Summary figures
// -------------------------------------------------------------------------

/**
 * The demo's `jbsum` strip (source line 3068). Six figures, in its order.
 *
 * "Waiting on a person" is the demo's own regex over the `next` sentence —
 * `/Approve|approval|Planner/i` — not a status field. Kept: the sentence is
 * the only place that fact is recorded, and re-deriving it from `stage` would
 * classify differently.
 */
export interface JobBoardSummary {
  readonly total: number;
  readonly critical: number;
  readonly waiting: number;
  readonly inProgress: number;
  readonly closed: number;
  readonly sites: number;
  readonly machines: number;
}

/** The demo's `/Approve|approval|Planner/i` test on a job's next action. */
export const waitingOnAPerson = (job: OemJob): boolean =>
  /Approve|approval|Planner/i.test(job.next);

export function jobBoardSummary(jobs: readonly OemJob[]): JobBoardSummary {
  return {
    total: jobs.length,
    critical: jobs.filter((j) => j.sev === 'crit').length,
    waiting: jobs.filter(waitingOnAPerson).length,
    inProgress: jobs.filter((j) => j.pct >= 40 && j.pct < 100 && j.sev !== 'done').length,
    closed: jobs.filter((j) => j.sev === 'done').length,
    sites: new Set(jobs.map((j) => j.mine)).size,
    machines: new Set(jobs.filter((j) => j.machine).map((j) => j.machine)).size,
  };
}

// -------------------------------------------------------------------------
// Module rows
// -------------------------------------------------------------------------

/** One module row on the summary — the demo's `jbrow`. */
export interface JobModuleRow {
  readonly id: JobStage;
  /** Two-digit poster number, as the demo pads it. */
  readonly n: string;
  readonly short: string;
  readonly name: string;
  readonly color: string;
  /** Jobs at this module, already sorted for the expanded card list. */
  readonly jobs: readonly OemJob[];
  readonly critical: number;
  readonly waiting: number;
  /** Mean progress across the module's jobs, 0 when it has none. */
  readonly avg: number;
  /** Top three kinds, as `"Alert ×7"`. */
  readonly kinds: readonly string[];
  /** Up to four machine ids (or leading id token), plus the overflow count. */
  readonly ids: readonly string[];
  readonly more: number;
}

/** The demo's severity sort key (source line 3055). */
const sevRank = (j: OemJob): number =>
  j.sev === 'crit' ? 0 : j.sev === 'warn' ? 1 : j.sev === 'info' ? 2 : 3;

/**
 * One row per module, in poster order, whether or not it has jobs.
 *
 * A module with nothing in it still gets a row: the board's job is to show the
 * whole operating model, and an empty Asset Base column is information.
 */
export function jobModuleRows(jobs: readonly OemJob[]): JobModuleRow[] {
  return MODULES.map((m: OemModule) => {
    const mine = jobs
      .filter((j) => j.stage === (m.id as JobStage))
      .slice()
      .sort((a, b) => sevRank(a) - sevRank(b) || b.pct - a.pct);

    const by = new Map<string, number>();
    for (const j of mine) by.set(j.kind, (by.get(j.kind) ?? 0) + 1);

    return {
      id: m.id as JobStage,
      n: String(m.n).padStart(2, '0'),
      short: m.short,
      name: m.name,
      color: m.col,
      jobs: mine,
      critical: mine.filter((j) => j.sev === 'crit').length,
      waiting: mine.filter(waitingOnAPerson).length,
      avg: mine.length
        ? Math.round(mine.reduce((a, j) => a + j.pct, 0) / mine.length)
        : 0,
      kinds: [...by.entries()]
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([kind, n]) => `${kind} ×${n}`),
      ids: mine.slice(0, 4).map((j) => j.machine ?? j.id.split(' ')[0]),
      more: Math.max(0, mine.length - 4),
    };
  });
}

// -------------------------------------------------------------------------
// Cards
// -------------------------------------------------------------------------

/** One job card, with the demo's two truncations already applied. */
export interface JobCard {
  /** React key — `id` alone repeats where a case appears under two rules. */
  readonly key: string;
  readonly job: OemJob;
  /** `"<machine> · <kind>"`, or just the kind on a fleet-level row. */
  readonly heading: string;
  /** `"<id> · <site short>"`. */
  readonly meta: string;
  /** Title, truncated at 70 characters as the demo truncates it. */
  readonly title: string;
  /** Next action, truncated at 64. */
  readonly next: string;
  /** Where the "run" link goes — a worked run, or a generated one. */
  readonly runKey: string;
  /** `true` when this job has one of the three worked traces. */
  readonly worked: boolean;
}

const cut = (s: string, n: number): string =>
  s.length > n ? `${s.slice(0, n - 2)}…` : s;

/** Card view models for a list of jobs, keyed uniquely. */
export const jobCards = (jobs: readonly OemJob[]): JobCard[] =>
  jobs.map((job, i) => ({
    key: `${job.id}#${i}`,
    job,
    heading: job.machine ? `${job.machine} · ${job.kind}` : job.kind,
    meta: `${job.id} · ${MSHORT[job.mine]}`,
    title: cut(job.title, 70),
    next: cut(job.next, 64),
    runKey: jobRunKey(job),
    worked: job.run !== null,
  }));
